import React from "react";
import { useI18n } from "@/i18n/I18nContext";
import { Reveal } from "@/components/Reveal";
import { SEO } from "@/components/SEO";
import { TAXIS, PUBLIC_TRANSPORT, EXPERIENCES } from "@/data/services";
import { Phone, Download, Bus, Ship, ExternalLink, Compass, Car } from "lucide-react";
import jsPDF from "jspdf";

const downloadTaxiPDF = (lang, S) => {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const M = 50, W = 595 - M * 2;
  let y = M + 10;
  doc.setFont("helvetica", "bold"); doc.setFontSize(22);
  doc.setTextColor(29, 53, 87);
  doc.text(S.taxiPdfTitle, M, y); y += 28;
  doc.setFont("helvetica", "normal"); doc.setFontSize(11); doc.setTextColor(90);
  const intro = doc.splitTextToSize(S.taxiPdfIntro, W);
  doc.text(intro, M, y); y += intro.length * 14 + 20;
  TAXIS.forEach((tx) => {
    if (y > 770) { doc.addPage(); y = M; }
    doc.setFont("helvetica", "bold"); doc.setFontSize(13); doc.setTextColor(29, 53, 87);
    const noteTxt = tx.note ? `  (${tx.note[lang] || tx.note.it})` : "";
    doc.text(`${tx.name}${noteTxt}`, M, y); y += 16;
    doc.setFont("helvetica", "normal"); doc.setFontSize(12); doc.setTextColor(40);
    doc.text(tx.phone, M, y); y += 24;
  });
  doc.setFontSize(8); doc.setTextColor(140);
  doc.text("B&B I Lake It Bellagio · ilakeitbellagio.it", M, 820);
  doc.save(`I-Lake-It-Bellagio-Taxi-${lang}.pdf`);
};

export default function ServicesPage() {
  const { t, lang } = useI18n();
  const S = {
    taxiPdfTitle: t("services.taxiPdfTitle"),
    taxiPdfIntro: t("services.taxiPdfIntro"),
  };

  return (
    <>
      <SEO pathKey="/servizi-trasporti" />

      <section className="pt-36 pb-12 bg-[#F2EFEB]">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Reveal>
            <h1 data-testid="services-title" className="font-display text-5xl md:text-6xl text-[#1A1A1A] tracking-tight">{t("services.title")}</h1>
            <p className="mt-5 text-lg text-[#5C5C5C] max-w-2xl">{t("services.subtitle")}</p>
          </Reveal>
        </div>
      </section>

      {/* Taxi contacts */}
      <section data-testid="taxi-section" className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-center mb-10">
              <Car className="w-8 h-8 text-[#1D3557] mx-auto mb-4" />
              <h2 className="font-display text-4xl md:text-5xl text-[#1A1A1A] tracking-tight">{t("services.taxiTitle")}</h2>
              <p className="mt-4 text-[#5C5C5C] max-w-2xl mx-auto leading-relaxed">{t("services.taxiIntro")}</p>
              <p data-testid="taxi-uber-note" className="mt-4 max-w-2xl mx-auto text-sm text-[#1D3557] bg-[#F2EFEB] border border-[#1D3557]/15 rounded-xl px-4 py-3 leading-relaxed">
                {t("services.uberNote")}
              </p>
              <button data-testid="download-taxi-pdf" onClick={() => downloadTaxiPDF(lang, S)}
                className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1D3557] text-white text-sm font-medium hover:bg-[#345B73] transition-all">
                <Download className="w-4 h-4" /> {t("services.taxiDownload")}
              </button>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {TAXIS.map((tx, i) => (
              <Reveal key={tx.name} delay={(i % 4) * 60}>
                <a data-testid={`taxi-${i}`} href={`tel:${tx.tel}`}
                  className="group flex items-center gap-4 bg-white p-5 rounded-2xl border border-black/5 hover:border-[#1D3557]/30 hover:shadow-sm transition-all h-full">
                  <span className="flex-shrink-0 w-11 h-11 rounded-full bg-[#1D3557]/10 flex items-center justify-center group-hover:bg-[#1D3557] transition-colors">
                    <Phone className="w-5 h-5 text-[#1D3557] group-hover:text-white transition-colors" />
                  </span>
                  <span className="min-w-0">
                    <span className="block font-display text-lg text-[#1A1A1A] truncate">{tx.name}</span>
                    {tx.note && <span className="block text-xs text-[#7A2B33]">{tx.note[lang] || tx.note.it}</span>}
                    <span className="block text-sm text-[#345B73] font-medium">{tx.phone}</span>
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Public transport */}
      <section data-testid="transport-section" className="py-16 md:py-24 bg-[#FAF9F6]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-center mb-10">
              <Bus className="w-8 h-8 text-[#1D3557] mx-auto mb-4" />
              <h2 className="font-display text-4xl md:text-5xl text-[#1A1A1A] tracking-tight">{t("services.transportTitle")}</h2>
              <p className="mt-4 text-[#5C5C5C] max-w-2xl mx-auto leading-relaxed">{t("services.transportIntro")}</p>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-5">
            {PUBLIC_TRANSPORT.map((p, i) => (
              <Reveal key={p.name} delay={i * 80}>
                <a data-testid={`transport-${i}`} href={p.url} target="_blank" rel="noopener noreferrer"
                  className="group block bg-white p-7 rounded-2xl border border-black/5 hover:border-[#1D3557]/30 hover:shadow-sm transition-all h-full">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <h3 className="font-display text-2xl text-[#1A1A1A] flex items-center gap-3">
                      <Ship className="w-5 h-5 text-[#345B73]" /> {p.name}
                    </h3>
                    <ExternalLink className="w-4 h-4 text-[#5C5C5C] group-hover:text-[#1D3557] transition-colors mt-1" />
                  </div>
                  <p className="text-[#5C5C5C] leading-relaxed">{p.desc[lang] || p.desc.it}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[#1D3557]">
                    {t("services.visitSite")} <ExternalLink className="w-3.5 h-3.5" />
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Experiences & Attractions */}
      <section data-testid="experiences-section" className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-center mb-10">
              <Compass className="w-8 h-8 text-[#1D3557] mx-auto mb-4" />
              <h2 className="font-display text-4xl md:text-5xl text-[#1A1A1A] tracking-tight">{t("services.experiencesTitle")}</h2>
              <p className="mt-4 text-[#5C5C5C] max-w-2xl mx-auto leading-relaxed">{t("services.experiencesIntro")}</p>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-5">
            {EXPERIENCES.map((ex, i) => (
              <Reveal key={ex.name} delay={(i % 2) * 80}>
                <a data-testid={`experience-${i}`} href={ex.url} target="_blank" rel="noopener noreferrer"
                  className="group flex flex-col bg-white rounded-2xl border border-black/5 overflow-hidden hover:border-[#1D3557]/30 hover:shadow-md transition-all h-full">
                  {ex.images && ex.images.length > 0 ? (
                    <div className={`grid ${ex.images.length > 1 ? "grid-cols-2" : "grid-cols-1"} gap-0.5 h-52 bg-[#F2EFEB]`}>
                      {ex.images.map((src, k) => (
                        <div key={k} className="overflow-hidden">
                          <img src={src} alt={ex.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="h-52 bg-[#1D3557] flex items-center justify-center">
                      <Car className="w-14 h-14 text-white/90" />
                    </div>
                  )}
                  <div className="p-7 flex flex-col flex-1">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <h3 className="font-display text-2xl text-[#1A1A1A]">{ex.name}</h3>
                      <ExternalLink className="w-4 h-4 text-[#5C5C5C] group-hover:text-[#1D3557] transition-colors mt-1.5 flex-shrink-0" />
                    </div>
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-[#F2EFEB] text-xs text-[#1A1A1A]">{ex.area}</span>
                      {ex.boat && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#1D3557]/10 text-xs text-[#1D3557]">
                          <Ship className="w-3.5 h-3.5" /> {t("services.byBoat")}
                        </span>
                      )}
                      {ex.car && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#1D3557]/10 text-xs text-[#1D3557]">
                          <Car className="w-3.5 h-3.5" /> {t("services.byCar")}
                        </span>
                      )}
                    </div>
                    <p className="text-[#5C5C5C] leading-relaxed">{ex.desc[lang] || ex.desc.it}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[#1D3557]">
                      {t("services.visitSite")} <ExternalLink className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
